import styles from "./styles.module.scss";
import {
  forwardRef,
  FunctionComponent,
  SVGProps,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Button from "components/Button";
import { IPackage } from "dto/IPackage";
import { ItemKind } from "dto/IKindItems";
import { IAttachToCaseFolderLink } from "api/casesService";
import { useNavigate } from "react-router-dom";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import HollowButton from "components/HollowButton";
import dossierService from "api/dossierService";
import { IDossierFolder } from "dto/Dossier/IDossierFolder";
import { TreeItem2Provider } from "@mui/x-tree-view/TreeItem2Provider";
import {
  TreeItem2Checkbox,
  TreeItem2Content,
  TreeItem2IconContainer,
  TreeItem2Label,
  TreeItem2Root,
} from "@mui/x-tree-view/TreeItem2";
import { TreeItem2Icon } from "@mui/x-tree-view/TreeItem2Icon";
import { useTreeItem2 } from "@mui/x-tree-view/useTreeItem2/useTreeItem2";
import FolderIcon from "assets/icons/folder_24px.svg?react";
import BackIcon from "assets/icons/arrowBack.svg?react";
import { treeItemClasses, useTreeItemState } from "@mui/x-tree-view/TreeItem";
import { styled, alpha } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { animated, useSpring } from "@react-spring/web";
import { TransitionProps } from "@mui/material/transitions";
import Box from "@mui/material/Box";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import { UseTreeItem2Parameters } from "@mui/x-tree-view/useTreeItem2";
import clsx from "clsx";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { IAttachment } from "dto/IAttachment";
import PackageAttachment from "components/PackageAttachment";
import { DossierItemType } from "dto/Dossier/DossierItemType";
import PDFIcon from "assets/icons/pdf.svg?react";
import XLSIcon from "assets/icons/xls.svg?react";
import OtherIcon from "assets/icons/other.svg?react";
import PngIcon from "assets/icons/png.svg?react";
import JpgIcon from "assets/icons/jpg.svg?react";
import DocIcon from "assets/icons/doc.svg?react";
import TXTIcon from "assets/icons/txt.svg?react";
import PptIcon from "assets/icons/ppt.svg?react";
import RarIcon from "assets/icons/rar.svg?react";
import ZipIcon from "assets/icons/zip.svg?react";
import PsdIcon from "assets/icons/psd.svg?react";
import SvgIcon from "assets/icons/svg.svg?react";
import GifIcon from "assets/icons/gif.svg?react";
import MovIcon from "assets/icons/mov.svg?react";
import classNames from "classnames";
import usePDF from "hooks/usePDF";
import PDFViewer from "components/PDFViewer";
import Spinner from "components/Spinner";
import { useDrag, useDrop } from "react-dnd";
import ListControl from "components/ListControl";
import { ListItem } from "components/ListControl/typings";
import settingsContext, { IOptionType } from "contexts/SettingsContext";
import { logger } from "utils/logger";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks/useTreeViewApiRef";
import { Tooltip } from "@mui/material";
import LayoutContext, { ILayoutSizes } from "contexts/LayoutContext";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import CloseIcon from "assets/icons/close.svg?react";
import { TagType } from "dto/ITag";
import tagsService from "api/tagsService";
import { useTranslation } from "react-i18next";

const AttachToCaseModalAttachments = ({
  toggle,
  c,
  pack,
  unusedAttachments,
  setUnusedAttachments,
  usedAttachments,
  setUsedAttachments,
  onSave,
  className,
  position,
}: {
  toggle: (value: boolean) => void;
  c: ICaseSimple;
  pack: IPackage;
  unusedAttachments: IAttachment[];
  setUnusedAttachments: (
    value: IAttachment[] | ((old: IAttachment[]) => IAttachment[])
  ) => void;
  usedAttachments: IAttachToCaseFolderLink[];
  setUsedAttachments: (
    value:
      | IAttachToCaseFolderLink[]
      | ((old: IAttachToCaseFolderLink[]) => IAttachToCaseFolderLink[])
  ) => void;
  onSave?: () => void;
  className?: string;
  position: "left" | "center";
}) => {
  const { t } = useTranslation();
  const [sizes, setSizes] = useState<ILayoutSizes>({
    width: 50,
    height: 50,
    isDraging: false,
  });

  const ref = useRef(null);
  const { options } = useContext(settingsContext);
  const { mutate: createFolder } = dossierService.useCreateFolder();

  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const [selectedItems, setSelectedItems] = useState<string | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<ListItem[]>(
    []
  );
  const { data: foldersData } = dossierService.useGetFolders(
    c?.Id ?? "none",
    c != null
  );
  const [folders, setFolders] = useState<IDossierItem[]>([]);
  const [foldersTmp, setFoldersTmp] = useState<IDossierItem[]>([]);

  // Domyślnie wszystkie załączniki są zaznaczone (checklista). Nowo dodane są
  // zaznaczane automatycznie; ręczne odznaczenia użytkownika są zachowywane.
  const prevUnusedIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const list = unusedAttachments ?? [];
    setSelectedAttachments((prev) => {
      const prevSel = new Set((prev as IAttachment[]).map((p) => p.Id));
      return list.filter(
        (a) => prevSel.has(a.Id) || !prevUnusedIdsRef.current.has(a.Id),
      );
    });
    prevUnusedIdsRef.current = new Set(list.map((a) => a.Id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unusedAttachments]);

  const { data: tagsData } = tagsService.useGetTags(false, TagType.Folder);

  const [usedAttachmentsTmp] = useState<
    IAttachToCaseFolderLink[]
  >([]);

  const [showUpdatePlugin] = useState<boolean>(false);
  const { blob, decryptPackageAndSetBlob, isLoading } = usePDF();

  const [newFolderName, setNewFolderName] = useState<string>("");

  const [newFolderParent, setNewFolderParent] = useState<IDossierItem>(null);
  const [newFolderItem, setNewFolderItem] = useState<IDossierItem>(null);
  const refNewFolderInput = useRef(null);
  const navigate = useNavigate();
  const apiRef = useTreeViewApiRef();

  // useOnClickOutside(refNewFolderInput, () => {
  //   commitNewFolder(newFolderItem, newFolderName);
  // });

  const rootItem: IDossierItem = {
    Id: c?.Id ?? "none",
    Label: "Dossier",
    CaseId: c?.Id ?? "none",

    Kind: ItemKind.DossierFolder,
    Type: DossierItemType.Folder,
    DeskInfo_IsOn: false,
    TagCount: 0,
    IsShared: false,
    CommentsCount: 0,
    CreationDate: new Date(),
    LastModified: new Date(),
    DossierId: c?.Id ?? "none",
    Keys: [],
    ObjectKeys: [],
    children: [],
  };

  const arrayToTree = (items: IDossierFolder[]) => {
    const rootItems: IDossierItem[] = [];
    const lookup: { [Id: string]: IDossierItem } = {};

    // Initialize lookup table and create a tree node for each item
    for (const item of items) {
      lookup[item.Id] = {
        ...item,
        Label: item.Name,
        children: [],
        Kind: ItemKind.DossierFolder,
        Type: DossierItemType.Folder,
        DeskInfo_IsOn: false,
        TagCount: 0,
        IsShared: false,
        CommentsCount: 0,
        CreationDate: new Date(),
        LastModified: new Date(),
        DossierId: item.Id,
        Keys: [],
        ObjectKeys: [],
      };
    }

    // Build the tree
    for (const item of items) {
      if (!item.ParentId) {
        rootItems.push(lookup[item.Id]);
      } else {
        const parent = lookup[item.ParentId!];
        if (parent) {
          parent.children!.push(lookup[item.Id]);
        }
      }
    }

    return rootItems;
  };

  const findElement = (
    id: string,
    items: IDossierItem[]
  ): IDossierItem | null => {
    const el = items.find((x) => x.Id === id);

    if (el) {
      return el;
    }

    for (let index = 0; index < items.length; index++) {
      const element = items[index];

      if (element.children) {
        const result = findElement(id, element.children);
        if (result) return result;
      }
    }

    return null;
  };

  const findElementParent = (
    id: string,
    items: IDossierItem[],
    parent: IDossierItem
  ): IDossierItem | null => {
    const el = items.find((x) => x.Id === id);

    if (el) {
      return parent;
    }

    for (let index = 0; index < items.length; index++) {
      const element = items[index];

      if (element.children) {
        const result = findElementParent(id, element.children, element);
        if (result) return result;
      }
    }

    return null;
  };

  const getAllItemsWithChildrenItemIds = () => {
    const itemIds: string[] = [];
    const registerItemId = (item: IDossierItem) => {
      if (item.children?.length) {
        itemIds.push(item.Id);
        item.children.forEach(registerItemId);
      }
    };

    folders.forEach(registerItemId);

    return itemIds;
  };

  const handleAddAttachment = (attachment: IAttachment) => {
    if (selectedItems) {
      const link = {
        AttachmentId: attachment.Id,
        FolderId: selectedItems != rootItem.Id ? selectedItems : null,
      };

      const unused = unusedAttachments.filter((x) => x.Id != attachment.Id);
      setUnusedAttachments(unused);
      setUsedAttachments([...usedAttachments, link]);

      const folderItem =
        selectedItems === rootItem.Id
          ? rootItem
          : findElement(selectedItems, folders);

      if (folderItem) {
        if (folderItem.children) {
          folderItem.children!.push({
            Id: attachment.Id,
            Label: attachment.Label ?? attachment.FileName,
            FileName: attachment.FileName,
            CaseId: c?.Id ?? "none",

            Kind: ItemKind.ContactMessage,
            Type: DossierItemType.Attachment,
            DeskInfo_IsOn: false,
            TagCount: 0,
            IsShared: false,
            CommentsCount: 0,
            CreationDate: new Date(),
            LastModified: new Date(),
            DossierId: c?.Id ?? "none",
            Keys: [],
            ObjectKeys: [],
          });
          setExpandedItems(getAllItemsWithChildrenItemIds());
        }
      }
    }
  };

  useEffect(() => {
    if (foldersData) {
      rootItem.children = arrayToTree(
        //@ts-ignorerefNewFolderInput
        foldersData as IDossierFolder[]
      );

      if (usedAttachments?.length > 0) {
        usedAttachments.forEach((link) => {
          const attachment = pack.Attachments.find(
            (x) => x.Id === link.AttachmentId
          );

          if (!link.FolderId) {
            rootItem.children!.push({
              Id: attachment.Id,
              Label: attachment.Label ?? attachment.FileName,
              FileName: attachment.FileName,
              CaseId: c?.Id ?? "none",

              Kind: ItemKind.ContactMessage,
              Type: DossierItemType.Attachment,
              DeskInfo_IsOn: false,
              TagCount: 0,
              IsShared: false,
              CommentsCount: 0,
              CreationDate: new Date(),
              LastModified: new Date(),
              DossierId: c?.Id ?? "none",
              Keys: [],
              ObjectKeys: [],
            });
          }
        });
      }

      setFolders([rootItem]);
      setFoldersTmp([rootItem]);
    }
  }, [foldersData]);

  useEffect(() => {
    setExpandedItems(getAllItemsWithChildrenItemIds());
  }, [folders]);

  const [, dropRef] = useDrop({
    accept: "attachment",
    drop: (d) => onListDrop(d.id),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: true,
    }),
  });

  const onListDrop = (attachmentId: string) => {
    const currentParent = findElementParent(attachmentId, folders, null);
    const currentElement = unusedAttachments.find((x) => x.Id === attachmentId);

    if (currentElement) return;

    if (currentParent != null) {
      const toRemove = currentParent.children.find((x) => x.Id === attachmentId);

      if (toRemove) {
        const index = currentParent.children.indexOf(toRemove, 0);
        if (index > -1) {
          currentParent.children.splice(index, 1);
        }
      }

      const toReturn = pack.Attachments.find((x) => x.Id === attachmentId);

      setUnusedAttachments([...unusedAttachments, toReturn]);
    }
  };

  const handleSelectedItemsChange = (
    event: React.SyntheticEvent,
    id: string | null
  ) => {
    setSelectedItems(id);
  };

  const handleExpandedItemsChange = (
    event: React.SyntheticEvent,
    itemIds: string[]
  ) => {
    setExpandedItems(itemIds);
  };

  const commitNewFolder = (item: IDossierItem, label: string) => {
    logger.log("Commiting new folder", newFolderItem);
    if (newFolderItem != null) {
      createFolder(
        {
          CaseId: newFolderParent.CaseId,
          ParentId:
            newFolderParent.Id != rootItem.Id ? newFolderParent.Id : null,
          Name: label,
        },
        {
          onSuccess: (d) => {
            const newFolders = [...folders];
            newFolderItem.Id = d.data.Id;
            newFolderItem.DossierId = d.data.Id;
            newFolderItem.CaseId = d.data.CaseId;
            newFolderItem.Label = label;
            newFolderItem.ParentId = d.data.ParentId;

            newFolderItem.isNew = false;
            setNewFolderItem(null);
            setNewFolderParent(null);

            setFolders(newFolders);
          },
        }
      );
    }
  };

  const cancelNewFolder = () => {
    if (newFolderItem != null) {
      newFolderParent.children.pop();
      setNewFolderItem(null);
      setNewFolderParent(null);
    }
  };

  const linkAttachmentToFolder = (
    attachment: IAttachment,
    folderItem: IDossierItem
  ) => {
    const link = {
      AttachmentId: attachment.Id,
      FolderId: folderItem.Id != rootItem.Id ? folderItem.Id : null,
    };

    setUnusedAttachments((old) => old.filter((x) => x.Id != attachment.Id));
    setUsedAttachments((old) => [...old, link]);

    if (folderItem) {
      if (folderItem.children) {
        folderItem.children!.push({
          Id: attachment.Id,
          Label: attachment.Label ?? attachment.FileName,
          FileName: attachment.FileName,
          CaseId: c?.Id ?? "none",

          Kind: ItemKind.ContactMessage,
          Type: DossierItemType.Attachment,
          DeskInfo_IsOn: false,
          TagCount: 0,
          IsShared: false,
          CommentsCount: 0,
          CreationDate: new Date(),
          LastModified: new Date(),
          DossierId: c?.Id ?? "none",
          Keys: [],
          ObjectKeys: [],
        });
        setExpandedItems(getAllItemsWithChildrenItemIds());
      }
    }
  };

  const handleCreateFolder = () => {
    setNewFolderName("");
    const folderItem = findElement(selectedItems, folders) ?? rootItem;
    logger.log("Creating folder, parent:", folderItem);

    if (!folderItem || folderItem.Type != DossierItemType.Folder) return;

    const newFolder: IDossierItem = {
      Id: "newItem",
      Label: t("dossier.newFolder"),
      CaseId: c?.Id ?? "none",

      Kind: ItemKind.DossierFolder,
      Type: DossierItemType.Folder,
      DeskInfo_IsOn: false,
      TagCount: 0,
      IsShared: false,
      CommentsCount: 0,
      CreationDate: new Date(),
      LastModified: new Date(),
      DossierId: c?.Id ?? "none",
      Keys: [],
      ObjectKeys: [],
      isNew: true,
      children: [],
    };

    folderItem.children!.push(newFolder);

    setNewFolderItem(newFolder);
    setNewFolderParent(folderItem);

    setExpandedItems(getAllItemsWithChildrenItemIds());
  };

  const StyledTreeItemRoot = styled(TreeItem2Root)(({ theme }) => ({
    color:
      theme.palette.mode === "light"
        ? theme.palette.grey[800]
        : theme.palette.grey[400],
    position: "relative",
    [`& .${treeItemClasses.groupTransition}`]: {
      marginLeft: theme.spacing(3.5),
    },
  })) as unknown as typeof TreeItem2Root;

  const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
    flexDirection: "row-reverse",
    borderRadius: theme.spacing(0.7),
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    background: "transparent",
    fontWeight: 500,
    [`&.Mui-expanded `]: {
      "&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon":
        {
          color:
            theme.palette.mode === "light"
              ? theme.palette.primary.main
              : theme.palette.primary.dark,
        },
      "&::before": {
        content: '""',
        display: "block",
        position: "absolute",
        left: "16px",
        top: "44px",
        height: "calc(100% - 48px)",
        width: "1.5px",
        backgroundColor:
          theme.palette.mode === "light"
            ? theme.palette.grey[300]
            : theme.palette.grey[700],
      },
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color:
        theme.palette.mode === "light" ? theme.palette.primary.main : "white",
    },
    [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
      backgroundColor:
        theme.palette.mode === "light" ? "#e6defe" : theme.palette.primary.dark,
      // color: theme.palette.primary.contrastText,
    },
  }));

  const AnimatedCollapse = animated(Collapse);

  function TransitionComponent(props: TransitionProps) {
    const style = useSpring({
      to: {
        opacity: props.in ? 1 : 0,
        transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
      },
    });

    return <AnimatedCollapse style={style} {...props} />;
  }

  const StyledTreeItemLabelText = styled(Typography)({
    color: "inherit",
    fontWeight: 500,
  }) as unknown as typeof Typography;

  interface CustomLabelProps {
    children: React.ReactNode;
    icon?: React.ElementType;
    expandable?: boolean;
  }

  function CustomLabel({
    icon: Icon,
    children,
    ...other
  }: CustomLabelProps) {
    return (
      <TreeItem2Label
        {...other}
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {Icon && (
          <Box
            component={Icon}
            className="labelIcon"
            color="inherit"
            sx={{ mr: 1, fontSize: "1.2rem" }}
          />
        )}

        <StyledTreeItemLabelText variant="body2">
          {children}
        </StyledTreeItemLabelText>
        {/* {expandable && <DotIcon />} */}
      </TreeItem2Label>
    );
  }

  const isExpandable = (reactChildren: React.ReactNode) => {
    if (Array.isArray(reactChildren)) {
      return reactChildren.length > 0 && reactChildren.some(isExpandable);
    }
    return Boolean(reactChildren);
  };

  const getIconFromFileType = (
    item: IDossierItem
  ): FunctionComponent<
    SVGProps<SVGSVGElement> & {
      title?: string;
    }
  > => {
    if (item.Type === DossierItemType.Folder) return FolderIcon;

    const name = item.FileName.split(".");
    const extenstion = name[name.length - 1]?.toLocaleLowerCase();

    switch (extenstion) {
      case "jpg":
        return JpgIcon;
      case "gif":
        return GifIcon;
      case "xlsx":
      case "xls":
        return XLSIcon;
      case "pdf":
        return PDFIcon;
      case "png":
        return PngIcon;
      case "doc":
      case "docx":
        return DocIcon;
      case "ppt":
      case "pptx":
        return PptIcon;
      case "txt":
        return TXTIcon;
      case "zip":
      case "7z":
        return ZipIcon;
      case "rar":
        return RarIcon;
      case "mov":
        return MovIcon;
      case "psd":
        return PsdIcon;
      case "svg":
        return SvgIcon;
      default:
        return OtherIcon;
    }
  };

  interface CustomTreeItemProps
    extends Omit<UseTreeItem2Parameters, "rootRef">,
      Omit<React.HTMLAttributes<HTMLLIElement>, "onFocus"> {}

  // „Powiąż zaznaczone" — wiąże zaznaczone (zachecklistowane) załączniki z folderem
  // wskazanym w drzewie „Dodaj do Dossier" (lub z korzeniem, gdy folder nie wybrany).
  const handleLinkSelected = () => {
    const sel = selectedAttachments as IAttachment[];
    if (!sel || sel.length === 0) return;
    const folderItem =
      selectedItems && selectedItems !== rootItem.Id
        ? findElement(selectedItems, folders)
        : rootItem;
    if (!folderItem) return;
    sel.forEach((a) => {
      const attachment = unusedAttachments.find((x) => x.Id === a.Id);
      if (attachment) linkAttachmentToFolder(attachment, folderItem);
    });
    setExpandedItems(getAllItemsWithChildrenItemIds());
    setSelectedAttachments([]);
  };

  const dropGlobal = (folderId: string, attachmentId: string) => {
    const folderItem = findElement(folderId, folders);

    if (!folderItem) {
      return;
    }

    if (selectedAttachments.length > 1) {
      selectedAttachments.forEach((a) => {
        const attachment = unusedAttachments.find((x) => x.Id === a.Id);
        if (!attachment) {
          return;
        }

        linkAttachmentToFolder(attachment, folderItem);
      });
    } else {
      console.log("Drop global", folderId, attachmentId);
      const attachment = unusedAttachments.find((x) => x.Id === attachmentId);

      if (!attachment) {
        // sprawdzamy, czy nie jest to przenoszenie na drzewie..
        const currentParent = findElementParent(attachmentId, folders, null);

        if (currentParent) {
          const toRemove = currentParent.children.find(
            (x) => x.Id === attachmentId
          );

          if (toRemove) {
            const index = currentParent.children.indexOf(toRemove, 0);
            if (index > -1) {
              currentParent.children.splice(index, 1);
            }

            folderItem.children.push(toRemove);
            setExpandedItems(getAllItemsWithChildrenItemIds());
          }
        }
        return;
      }

      linkAttachmentToFolder(attachment, folderItem);
    }
  };

  const CustomTreeItem = forwardRef(function CustomTreeItem(
    props: CustomTreeItemProps,
    ref: React.Ref<HTMLLIElement>
  ) {
    const {
      children,
      itemId,
      id,
      label,
      ...other
    } = props;

    const { disabled } =
      useTreeItemState(itemId);

    const {
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getCheckboxProps,
      getLabelProps,
      getGroupTransitionProps,
      status,
      publicAPI,
    } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

    const contentRef = useRef(null);
    // @ts-ignore
    const item = publicAPI.getItem(itemId);
    const expandable = isExpandable(children);

    const [newFolderFocused, setNewFolderFocused] = useState<boolean>(false);
    const onDrop = (d) => {
      dropGlobal(itemId, d.id);
    };

    const [{ isOver }, drop] = useDrop({
      accept:
        item?.Type !== DossierItemType.Attachment ? ["attachment"] : "none",
      drop: onDrop,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    });

    const [, dragRef] = useDrag(
      () => ({
        type: "attachment",
        canDrag: () => item?.Type === DossierItemType.Attachment,
        item: { id: item?.Id },
        collect: (monitor) => ({
          opacity: monitor.isDragging() ? 0.5 : 1,
        }),
      }),
      []
    );

    // const handleAttach = () => {
    //   setShowAttachments(true);
    //   if (attListRef.current) {
    //     var parent = parentRef.current.getBoundingClientRect();
    //     var row = iconRef.current.getBoundingClientRect();
    //     setModalMode(row.y - parent.y + 28);
    //   }
    //   setSelectedItems(props.id);
    // };

    let icon = FolderIcon;
    icon = getIconFromFileType(item);

    return (
      // @ts-ignore
      <TreeItem2Provider itemId={itemId} key={"k" + itemId}>
        <StyledTreeItemRoot
          key={"root" + itemId}
          {...getRootProps({
            other,
          })}
        >
          <div ref={drop}>
            <div ref={dragRef}>
              <CustomTreeItemContent
                key={"content" + itemId}
                ref={contentRef}
                {...getContentProps({
                  className: clsx("content", {
                    "Mui-expanded": status.expanded,
                    "Mui-selected": status.selected,
                    "Mui-focused": status.focused || isOver,
                    "Mui-disabled": status.disabled,
                  }),
                })}
              >
                <TreeItem2IconContainer {...getIconContainerProps()}>
                  <TreeItem2Icon status={status} />
                </TreeItem2IconContainer>
                <TreeItem2Checkbox {...getCheckboxProps()} />

                {!item.isNew && (
                  <CustomLabel
                    {...getLabelProps({
                      icon,
                      expandable: expandable && status.expanded,
                    })}
                  />
                )}

                {item.isNew && (
                  <div className={styles.inlineEditor}>
                    <input
                      ref={refNewFolderInput}
                      autoFocus={true}
                      className={styles.newFolderInput}
                      key="input_newFolderrr"
                      defaultValue={newFolderName}
                      onFocus={() => {
                        setNewFolderFocused(true);
                      }}
                      onBlur={(e) => {
                        setNewFolderName(e.target.value);
                        setNewFolderFocused(false);
                      }}
                      placeholder={t("dossier.newFolder")}
                      id="input_newFolder"
                      onKeyDownCapture={(e) => {
                        if (e.key === "Enter") {
                          const i = e.target as HTMLInputElement;
                          commitNewFolder(item, i.value);
                        } else if (e.key === "Escape") {
                          cancelNewFolder();
                        }
                        e.stopPropagation();
                      }}
                      onClickCapture={(e) => e.stopPropagation()}
                    ></input>

                    {newFolderFocused && tagsData?.data?.length > 0 && (
                      <div className={styles.dropdown}>
                        {tagsData?.data.map((tag) => (
                          <div
                            key={tag.Id}
                            className={styles.itm}
                            onMouseDown={() => {
                              console.log("Click");
                              setNewFolderName(tag.Name);

                              refNewFolderInput.current.value = tag.Name;
                              setNewFolderFocused(false);
                            }}
                          >
                            {tag.Name}
                          </div>
                        ))}
                      </div>
                    )}

                    <HollowButton
                      className={styles.smallButton}
                      text={t("common.cancel")}
                      onClick={() => {
                        cancelNewFolder();
                      }}
                    ></HollowButton>
                    <Button
                      text={t("common.save")}
                      onClick={() =>
                        commitNewFolder(
                          item,
                          (refNewFolderInput.current as HTMLInputElement).value
                        )
                      }
                      className={styles.smallButton}
                    ></Button>
                  </div>
                )}
              </CustomTreeItemContent>
              {/* {provided.placeholder} */}
            </div>
          </div>
          {children && <TransitionComponent {...getGroupTransitionProps()} />}
        </StyledTreeItemRoot>
      </TreeItem2Provider>
    );
  });

  useEffect(() => {
    if (!selectedItems) {
      setSelectedItems(rootItem.Id);
    }
  }, [rootItem]);

  return (
    <div
      className={classNames(
        styles.dossierWrapper,
        showPreview ? styles.withPreview : null,
        className
      )}
    >
      <div
        className={classNames(
          styles.main,
          position === "left" ? styles.onLeft : "eeee"
        )}
      >
        <div
          className={classNames(
            styles.hdr,
            position === "center" ? styles.center : styles.left
          )}
        >
          <div
            className={styles.title}
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <span>{t("documents.attachments")}</span>
            <HollowButton
              text="Powiąż zaznaczone"
              disabled={selectedAttachments.length === 0}
              onClick={() => handleLinkSelected()}
              className={styles.hollowButton}
            />
          </div>{" "}
          <CloseIcon
            onClick={() => {
              toggle(false);
            }}
            className={styles.closeIcon}
          />
        </div>
        <div className={styles.subtitle}>{t("attachCase.unlinkedAttachmentsList")}</div>
        <div className={styles.grow}>
          <LayoutContext.Provider
            value={{
              sizes,
              setSizes,
            }}
          >
            <PanelGroup ref={ref} direction="vertical">
              <Panel defaultSize={50}>
                <div className={classNames(styles.list)} ref={dropRef}>
                  <ListControl
                    getItemId={(i) => i.Id}
                    items={unusedAttachments}
                    renderItem={(i) => {
                      const attach = i as IAttachment;

                      return (
                        <PackageAttachment
                          className={styles.attachment}
                          key={attach.Id}
                          onDownload={() => {}}
                          onPreview={() => {}}
                          handleDoubleClick={() => {
                            const previewLocation = options.find(
                              (x) =>
                                x.Type ===
                                IOptionType.caseAttachAttachmentsLocation
                            );

                            if (
                              !previewLocation ||
                              previewLocation.Value !== "left"
                            ) {
                              decryptPackageAndSetBlob({
                                FileName: attach.FileName,
                                EncryptionKeyId: attach.EncryptionKeyId,
                                Uri: attach.Uri,
                                Kind: pack.Kind,
                              });
                              setShowPreview(true);
                            } else {
                              const newUrl = `${window.location.pathname}?pdf=${
                                pack.Id
                              }:${attach.FileName.replace(":", "")}:${
                                attach.Uri ?? attach.Id
                              }:${attach.Kind}:${attach.EncryptionKeyId}`;
                              navigate(newUrl);
                            }
                          }}
                          onNewWindow={() => {}}
                          attachment={attach}
                          isInDossier={Boolean(attach.ShowInDossier)}
                          onAddDossier={handleAddAttachment}
                          disableMenu={true}
                          sizeInColumn={true}
                        />
                      );
                    }}
                    selectedItems={selectedAttachments}
                    setSelectedItems={setSelectedAttachments}
                    checklist
                  ></ListControl>
                  {unusedAttachments.length === 0 && (
                    <div className={styles.empty}>
                      {t("attachCase.allAttachmentsLinked")}
                    </div>
                  )}
                </div>
              </Panel>
              <PanelResizeHandle
                onDragging={(isDraging) => {
                  setSizes({ ...sizes, isDraging });
                }}
                className={`${styles.splitter}`}
                id={"attachAttachments"}
              ></PanelResizeHandle>

              <Panel defaultSize={50}>
                <div className={classNames(styles.listWrapper)}>
                  <div className={styles.buttons}>
                    <HollowButton
                      text={`+ ${t("dossier.newFolder")}`}
                      onClick={(e) => {
                        e?.preventDefault();
                        e?.stopPropagation();
                        handleCreateFolder();
                      }}
                      disabled={!selectedItems}
                      className={styles.hollowButton}
                    ></HollowButton>
                  </div>
                  <div className={classNames(styles.list)}>
                    <RichTreeView
                      apiRef={apiRef}
                      items={folders}
                      getItemId={(x: IDossierItem) => x.Id}
                      getItemLabel={(x: IDossierItem) => x.Label ?? ""}
                      slots={{ item: CustomTreeItem }}
                      defaultExpandedItems={[c?.Id, rootItem.Id]}
                      expandedItems={expandedItems}
                      onExpandedItemsChange={handleExpandedItemsChange}
                      expansionTrigger="iconContainer"
                      multiSelect={false}
                      selectedItems={selectedItems}
                      onSelectedItemsChange={handleSelectedItemsChange}
                      onItemFocus={() => {
                        const input =
                          document.getElementById(`input_newFolder`);
                        if (input) input.focus();
                      }}
                    />
                  </div>
                </div>
              </Panel>
            </PanelGroup>
          </LayoutContext.Provider>
        </div>

        {/* <div
              className={styles.attList}
              ref={attListRef}
              style={{
                display: showAttachments ? "block" : "none",
                top: modalMove,
              }}
            >
              {unusedAttachments &&
                unusedAttachments.map((attach) => (
                  <PackageAttachment
                    className={styles.attachment}
                    key={attach.Id}
                    onDownload={(attach) => {}}
                    onPreview={(attach) => {
                      //setPreviewItem(attach);
                      setShowPreview(true);
                    }}
                    onNewWindow={() => {}}
                    attachment={attach}
                    isInDossier={Boolean(attach.ShowInDossier)}
                    onAddDossier={handleAddAttachment}
                  />
                ))}
              <div className={styles.buttons}>
                <Button
                  text="+ Wszystkie"
                  onClick={handleAddAll}
                ></Button>
              </div>
            </div> */}

        <div className={classNames(styles.footer, styles.noborder)}>
          <div className={styles.buttonWrapper}>
            <HollowButton
              onClick={() => {
                setUsedAttachments(usedAttachmentsTmp);
                setFolders(foldersTmp);
                toggle(false);
              }}
              text={t("common.cancel")}
            />
            <Button
              disabled={!c}
              onClick={() => {
                onSave?.();
                toggle(false);
              }}
              text={t("common.add")}
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.preview, showPreview ? styles.show : null)}
      >
        <div className={styles.toolbarFake}>
          <HollowButton
            text=""
            element={
              <Tooltip title={t("tooltips.closePreview")}>
                <div>
                  <BackIcon></BackIcon>
                </div>
              </Tooltip>
            }
            onClick={() => setShowPreview(false)}
          ></HollowButton>
        </div>
        <div className={styles.content}>
          {isLoading && (
            <div className={styles.loading}>
              <Spinner></Spinner>
            </div>
          )}
          {!isLoading && blob && !showUpdatePlugin && (
            <PDFViewer
              className={styles.viewer}
              file={blob}
              sourceFile={blob}
              showHeader={true}
              inModal={true}
              headerClassName={styles.pdfHeader}
              title={""}
              onCloseButton={() => setShowPreview(false)}
              withoutBorder={true}
              onlyPDFButtons={true}
            />
          )}
          {!isLoading && showUpdatePlugin && (
            <PDFViewer
              className={styles.viewer}
              file={new Blob([])}
              showHeader={true}
              headerClassName={styles.title}
              showVersion={true}
              showTitle={true}
              title={t("common.preview")}
              onCloseButton={() => setShowPreview(false)}
              withoutBorder={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachToCaseModalAttachments;
